import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import SEOHead from "./ui/SEOHead";
import { ToastProvider } from "../components/ui/use-toast";

export default function LandingPage() {
  return (
    <HelmetProvider>
      <SEOHead
        title="Welcome to Cov Properties - Your Trusted Real Estate Partner"
        description="Connect with Cov Properties - Dubai's premier real estate experts. Follow us on social media and visit our main website for the latest properties and insights."
      />
      <ToastProvider>
        <main className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <div className="mb-8">
                <h1 className="text-6xl font-bold text-slate-800 mb-4">
                Coverdale Properties
                </h1>
                <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
                <p className="text-xl text-slate-600 font-light">
                  Your trusted real estate partner in UAE
                </p>
              </div>
            </div>

            {/* Main Website Button */}
            <div className="text-center mb-12">
              <a
                href="https://en.covproperties.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0 0V3" />
                </svg>
                Visit Our Main Website
              </a>
            </div>

            {/* Social Media Section */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <h2 className="text-2xl font-semibold text-slate-800 text-center mb-8">
                Connect With Us
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/covproperties"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-6 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Instagram</h3>
                    <p className="text-white/80">@covproperties</p>
                  </div>
                </a>

                {/* YouTube */}
                <a
                  href="https://www.youtube.com/@CovProperties/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-6 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">YouTube</h3>
                    <p className="text-white/80">Coverdale Properties</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Footer */}
            <footer className="text-center text-slate-600">
              <div className="flex justify-center items-center space-x-6 mb-4">
                <a 
                  href="mailto:info@covproperties.com" 
                  className="hover:text-blue-600 transition-colors duration-300 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  info@covproperties.com
                </a>
                <span className="text-slate-400">|</span>
                <a 
                  href="tel:+971-56-971-7003" 
                  className="hover:text-blue-600 transition-colors duration-300 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +971 56 971 7003
                </a>
              </div>
              <p className="text-sm">
                © 2025 Cov Properties. All rights reserved.
              </p>
            </footer>
          </div>
        </main>
      </ToastProvider>
    </HelmetProvider>
  );
} 